// Right-hand panel scaffolding. Phase 7 will wire real cost / IaC / version /
// share content + plan gating; today these are honest placeholders so the
// layout shell is correct and seams are obvious.

import { Lock, Sparkles, Clock, Share2, RotateCcw, GitCompare } from "lucide-react";
import { summarizeHierarchyCosts } from "../../lib/costSummary";

function PanelSection({ title, icon, children, testid }) {
  return (
    <section
      data-testid={testid}
      style={{
        background: "var(--color-background-secondary)",
        border: "1px solid var(--color-border-primary)",
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 12,
      }}
    >
      <div
        className="flex items-center gap-2"
        style={{ marginBottom: 10 }}
      >
        {icon}
        <h3
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--color-text-primary)",
            letterSpacing: "-0.005em",
          }}
        >
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

function LockedCard({ message, cta }) {
  return (
    <div
      style={{
        background: "var(--color-background-tertiary)",
        border: "1px dashed var(--color-border-primary)",
        borderRadius: 10,
        padding: "16px 14px",
        textAlign: "center",
      }}
    >
      <Lock size={16} style={{ margin: "0 auto", color: "var(--color-text-tertiary)" }} />
      <p
        style={{
          fontSize: 12.5,
          color: "var(--color-text-secondary)",
          marginTop: 8,
          marginBottom: 12,
          lineHeight: 1.5,
        }}
      >
        {message}
      </p>
      <a
        href="/pricing"
        className="motion-micro inline-flex items-center gap-1.5"
        style={{
          background: "var(--color-accent)",
          color: "#0a0a0a",
          fontSize: 12,
          fontWeight: 600,
          padding: "7px 14px",
          borderRadius: 8,
          textDecoration: "none",
        }}
      >
        {cta}
      </a>
    </div>
  );
}

export default function OutputRightPanel({ arch, terraformCoverage, onRestoreVersion, onOpenDiff, onExportTerraform, onExportBicep, onShare, onRevokeShare, shareUrl }) {
  const plan = arch?.plan || "free";
  const isFree = plan === "free";
  const isTeam = plan === "team";
  const isAzure = arch?.provider === "azure";
  const hierarchyCount = (arch?.nodes || []).length;

  const { total: totalCost, componentCount, byTier: costByTier } =
    summarizeHierarchyCosts(arch?.nodes || [], arch?.provider);
  const TIER_COLOR = {
    frontend: "#5B5BD6",
    compute: "#0EA5E9",
    data: "#0D9488",
    networking: "#F59E0B",
    security: "#EF4444",
  };
  const tierOrder = ["compute", "data", "networking", "frontend", "security"];

  return (
    <aside
      data-testid="output-right-panel"
      style={{
        width: 360,
        height: "100%",
        borderLeft: "1px solid var(--color-border-primary)",
        background: "var(--color-background-primary)",
        overflowY: "auto",
        padding: "20px 18px",
        flexShrink: 0,
      }}
    >
      <PanelSection
        testid="panel-cost-estimate"
        title="Cost Estimate"
        icon={<Sparkles size={14} color="var(--color-accent)" />}
      >
          <>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "var(--color-text-primary)",
              }}
              data-testid="panel-cost-total"
            >
              ${totalCost.toLocaleString()}
              <span
                style={{
                  fontSize: 13,
                  color: "var(--color-text-tertiary)",
                  fontWeight: 500,
                  marginLeft: 4,
                }}
              >
                /mo
              </span>
            </div>
            <p
              style={{
                fontSize: 11.5,
                color: "var(--color-text-tertiary)",
                marginTop: 6,
              }}
            >
              Sum of {componentCount} priced components across Cloud and Network views.
            </p>
            {isFree && <p className="text-text-tertiary mt-2" style={{ fontSize: 12 }}>
              Basic estimate. Upgrade to Pro for the full cost breakdown.
            </p>}
            {!isFree && totalCost > 0 && (
              <div data-testid="panel-cost-breakdown" style={{ marginTop: 14 }}>
                {tierOrder.concat(Object.keys(costByTier).filter((t) => !tierOrder.includes(t))).map((tier) => {
                  const v = costByTier[tier] || 0;
                  if (!v) return null;
                  const pct = totalCost > 0 ? Math.round((v / totalCost) * 100) : 0;
                  const color = TIER_COLOR[tier] || "#64748B";
                  return (
                    <div key={tier} data-testid={`panel-cost-tier-${tier}`} style={{ marginBottom: 8 }}>
                      <div className="flex items-center justify-between gap-2" style={{ marginBottom: 4 }}>
                        <span
                          className="font-mono"
                          style={{
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color,
                            fontWeight: 600,
                          }}
                        >
                          {tier === "networking" ? "Network (included in total)" : tier}
                        </span>
                        <span style={{ fontSize: 11.5, color: "var(--color-text-secondary)", fontFamily: "JetBrains Mono, monospace" }}>
                          ${v.toLocaleString()} · {pct}%
                        </span>
                      </div>
                      <div
                        style={{
                          height: 4,
                          background: "var(--color-background-tertiary)",
                          borderRadius: 999,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${pct}%`,
                            background: color,
                            transition: "width 200ms ease",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
      </PanelSection>

      <PanelSection
        testid="panel-iac-export"
        title="IaC Export"
        icon={<Sparkles size={14} color="var(--color-accent)" />}
      >
        {isFree ? (
          <LockedCard
            message="Terraform & Bicep exports are available on Pro."
            cta="Upgrade to Pro"
          />
        ) : (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              data-testid="panel-export-terraform"
              onClick={onExportTerraform}
              className="motion-micro"
              style={{
                background: "var(--color-background-tertiary)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-primary)",
                fontSize: 12.5,
                fontWeight: 500,
                padding: "9px 12px",
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              Download Terraform (.tf)
            </button>
            {isAzure && (
              <button
                type="button"
                data-testid="panel-export-bicep"
                onClick={onExportBicep}
                className="motion-micro"
                style={{
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-primary)",
                  fontSize: 12.5,
                  fontWeight: 500,
                  padding: "9px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                Download Bicep (.bicep)
              </button>
            )}
          </div>
        )}
        <div data-testid="panel-terraform-coverage" style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 10 }}>
          <span data-testid="panel-architecture-node-count" className="font-mono"
            style={{ display: "inline-block", border: "1px solid var(--color-border-primary)", borderRadius: 6, padding: "3px 6px", marginBottom: 7 }}>
            Hierarchy: {hierarchyCount} {hierarchyCount === 1 ? "node" : "nodes"}
          </span>
          {terraformCoverage?.error ? (
            <p>Terraform coverage unavailable. Review the downloaded file before use.</p>
          ) : terraformCoverage ? (
            <>
              <p>Terraform: {terraformCoverage.mapped} mapped · {terraformCoverage.stubbed} TODO {terraformCoverage.stubbed === 1 ? "stub" : "stubs"}
                {terraformCoverage.omitted > 0 ? ` · ${terraformCoverage.omitted} omitted` : ""}
              </p>
              {terraformCoverage.mapped + terraformCoverage.stubbed + terraformCoverage.omitted !== hierarchyCount && (
                <p role="alert">Terraform coverage is out of sync with this hierarchy.</p>
              )}
              {terraformCoverage.stubbed_resources?.length > 0 && (
                <ul data-testid="panel-terraform-stubs" style={{ marginTop: 6, paddingLeft: 16, maxHeight: 150, overflowY: "auto" }}>
                  {terraformCoverage.stubbed_resources.map((item) => (
                    <li key={item.id}>{item.name} ({item.type}) — TODO</li>
                  ))}
                </ul>
              )}
              {isAzure && terraformCoverage.bicep && (
                <p data-testid="panel-bicep-coverage" style={{ marginTop: 6 }}>
                  Bicep: {terraformCoverage.bicep.mapped} mapped · {terraformCoverage.bicep.stubbed} TODO {terraformCoverage.bicep.stubbed === 1 ? "stub" : "stubs"} · {terraformCoverage.bicep.omitted} omitted · {terraformCoverage.bicep.supporting} supporting
                </p>
              )}
              {isAzure && terraformCoverage.bicep?.stubbed_resources?.length > 0 && (
                <ul data-testid="panel-bicep-stubs" style={{ marginTop: 6, paddingLeft: 16, maxHeight: 150, overflowY: "auto" }}>
                  {terraformCoverage.bicep.stubbed_resources.map((item) => (
                    <li key={item.id}>{item.name} ({item.type}) — TODO</li>
                  ))}
                </ul>
              )}
              {isAzure && terraformCoverage.bicep && (
                terraformCoverage.bicep.mapped + terraformCoverage.bicep.stubbed + terraformCoverage.bicep.omitted !== hierarchyCount) && (
                <p role="alert">Bicep coverage is out of sync with this hierarchy.</p>
              )}
              <p style={{ marginTop: 6 }}>Partial scaffold. Mapped blocks still need review before apply.</p>
            </>
          ) : (
            <p>Checking Terraform coverage…</p>
          )}
        </div>
      </PanelSection>

      <PanelSection
        testid="panel-version-history"
        title="Version History"
        icon={<Clock size={14} color="var(--color-text-secondary)" />}
      >
        {onOpenDiff && (arch?.versions || []).length >= 2 && (
          <button
            type="button"
            onClick={onOpenDiff}
            data-testid="panel-open-diff"
            className="motion-micro inline-flex items-center gap-1.5"
            style={{
              background: "var(--color-background-tertiary)",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              fontSize: 11.5,
              fontWeight: 500,
              padding: "6px 10px",
              borderRadius: 8,
              cursor: "pointer",
              marginBottom: 10,
              width: "100%",
              justifyContent: "center",
            }}
          >
            <GitCompare size={12} /> Compare versions…
          </button>
        )}
        <div className="flex flex-col gap-2">
          {(arch?.versions || [])
            .slice()
            .reverse()
            .map((v, i) => {
              const isCurrent = i === 0;
              return (
                <div
                  key={v.version}
                  data-testid={`panel-version-${v.version}`}
                  style={{
                    fontSize: 12,
                    padding: "10px 10px",
                    borderRadius: 8,
                    background: "var(--color-background-tertiary)",
                    border: "1px solid var(--color-border-primary)",
                  }}
                >
                  <div
                    className="flex items-center justify-between gap-2"
                    style={{ marginBottom: isCurrent ? 0 : 6 }}
                  >
                    <div className="min-w-0">
                      <div style={{ color: "var(--color-text-primary)", fontWeight: 500 }}>
                        v{v.version}
                        {v.label && (
                          <span
                            style={{
                              color: "var(--color-text-tertiary)",
                              marginLeft: 6,
                              fontSize: 11,
                            }}
                          >
                            {v.label}
                          </span>
                        )}
                      </div>
                      <div
                        className="font-mono"
                        style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}
                      >
                        {new Date(v.createdAt).toLocaleString()}
                      </div>
                    </div>
                    {isCurrent && (
                      <span
                        className="font-mono"
                        style={{
                          fontSize: 10,
                          color: "var(--color-accent)",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                        data-testid={`panel-version-${v.version}-current`}
                      >
                        ← current
                      </span>
                    )}
                  </div>
                  {!isCurrent && onRestoreVersion && (
                    <button
                      type="button"
                      onClick={() => onRestoreVersion(v)}
                      data-testid={`panel-version-${v.version}-restore`}
                      className="motion-micro inline-flex items-center gap-1.5"
                      style={{
                        background: "transparent",
                        border: "1px solid var(--color-border-primary)",
                        color: "var(--color-text-secondary)",
                        fontSize: 11,
                        fontWeight: 500,
                        padding: "5px 10px",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "var(--color-accent)";
                        e.currentTarget.style.color = "var(--color-accent)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "var(--color-border-primary)";
                        e.currentTarget.style.color = "var(--color-text-secondary)";
                      }}
                    >
                      <RotateCcw size={11} /> Restore
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      </PanelSection>

      {isTeam && (
        <PanelSection
          testid="panel-share"
          title="Shareable Link"
          icon={<Share2 size={14} color="var(--color-text-secondary)" />}
        >
          {shareUrl ? (
            <>
              <p
                style={{
                  fontSize: 11.5,
                  color: "var(--color-text-tertiary)",
                  marginBottom: 6,
                }}
              >
                Anyone with this link can view (no edits).
              </p>
              <div
                data-testid="panel-share-url"
                style={{
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 11,
                  color: "var(--color-text-primary)",
                  wordBreak: "break-all",
                  marginBottom: 8,
                }}
              >
                {shareUrl}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="panel-share-copy"
                  onClick={async () => {
                    try {
                      await navigator.clipboard?.writeText(shareUrl);
                    } catch {
                      // No clipboard permission — user can select+copy manually.
                    }
                  }}
                  className="motion-micro"
                  style={{
                    background: "var(--color-accent)",
                    color: "#0a0a0a",
                    border: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "7px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  Copy link
                </button>
                <button
                  type="button"
                  data-testid="panel-share-revoke"
                  onClick={onRevokeShare}
                  className="motion-micro"
                  style={{
                    background: "transparent",
                    border: "1px solid var(--color-border-primary)",
                    color: "var(--color-text-secondary)",
                    fontSize: 12,
                    fontWeight: 500,
                    padding: "7px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  Revoke
                </button>
              </div>
            </>
          ) : (
            <>
              <p
                style={{
                  fontSize: 12,
                  color: "var(--color-text-secondary)",
                  marginBottom: 8,
                }}
              >
                Generate a read-only link to share this architecture.
              </p>
              <button
                type="button"
                data-testid="panel-share-generate"
                onClick={onShare}
                className="motion-micro"
                style={{
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-primary)",
                  fontSize: 12.5,
                  fontWeight: 500,
                  padding: "9px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                Generate share link
              </button>
            </>
          )}
        </PanelSection>
      )}
    </aside>
  );
}
